import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAccountSettings } from '../store/accountThunk';

// Mirrors ibook-backend/src/utils/partyFormConfig.js.
export type PartyType = 'buyer' | 'supplier';

export const HIDEABLE_FIELDS: Record<PartyType, { key: string; label: string }[]> = {
    buyer: [
        { key: 'website', label: 'Website' },
        { key: 'contactName', label: 'Contact Name' },
        { key: 'email', label: 'Email' },
        { key: 'whatsAppNumber', label: 'WhatsApp Number' },
        { key: 'dateOfBirth', label: 'Date of Birth' },
        { key: 'age', label: 'Age' },
        { key: 'gender', label: 'Gender' },
        { key: 'shippingAddress', label: 'Shipping Address' },
        { key: 'familyMembers', label: 'Family Members' },
        { key: 'registrationNo', label: 'Company Registration Number' },
        { key: 'stateOfRegistration', label: 'State of Registration' },
        { key: 'countryOfRegistration', label: 'Country of Registration' },
        { key: 'taxIdentificationNumber1', label: 'PAN Number' },
        { key: 'openingBalance', label: 'Opening Balance' },
    ],
    supplier: [
        { key: 'website', label: 'Website' },
        { key: 'contactName', label: 'Contact Name' },
        { key: 'email', label: 'Email' },
        { key: 'whatsAppNumber', label: 'WhatsApp Number' },
        { key: 'shippingAddress', label: 'Shipping Address' },
        { key: 'familyMembers', label: 'Family Members' },
        { key: 'bankDetails', label: 'Bank Details' },
        { key: 'registrationNo', label: 'Company Registration Number' },
        { key: 'stateOfRegistration', label: 'State of Registration' },
        { key: 'countryOfRegistration', label: 'Country of Registration' },
        { key: 'taxIdentificationNumber1', label: 'PAN Number' },
        { key: 'openingBalance', label: 'Opening Balance' },
    ],
};

export const UNIQUE_FIELDS = [
    { key: 'name', label: 'Name' },
    { key: 'legalName', label: 'Legal/Business Name' },
    { key: 'whatsAppNumber', label: 'WhatsApp Number' },
    { key: 'registrationNo', label: 'Company Registration Number' },
    { key: 'taxIdentificationNumber1', label: 'PAN Number' },
    { key: 'taxIdentificationNumber2', label: 'GST Number' },
];

// Sections hidden until an admin turns them on (mirrors backend).
export const OPT_IN_FIELDS = ['familyMembers'];

export const ALWAYS_UNIQUE = ['taxIdentificationNumber2'];
const DEFAULT_UNIQUE = ['name', 'legalName', 'taxIdentificationNumber1', 'taxIdentificationNumber2'];

export interface PartyFormConfig { hiddenFields: string[]; uniqueFields: string[]; gstAutofillTop: boolean }

export const resolvePartyForm = (settings: any, type: PartyType): PartyFormConfig => {
    const stored = settings?.partyForm?.[type] || {};
    const unique: string[] = Array.isArray(stored.uniqueFields) ? stored.uniqueFields : DEFAULT_UNIQUE;
    return {
        hiddenFields: [
            ...(stored.hiddenFields || []).filter((f: string) => !OPT_IN_FIELDS.includes(f)),
            ...OPT_IN_FIELDS.filter((f) => !(stored.optInFields || []).includes(f)),
        ],
        uniqueFields: Array.from(new Set([...unique, ...ALWAYS_UNIQUE])),
        gstAutofillTop: stored.gstAutofillTop !== false,
    };
};

// Account owner or Admin/Super Admin role (role names vary in case).
export const useIsAccountAdmin = () => {
    const { user, modulePermissions } = useAppSelector((state: any) => state.auth);
    return !!modulePermissions?.isAccountOwner
        || !!user?.role?.some((r: any) => ['admin', 'superadmin', 'owner'].includes(String(r?.role || '').toLowerCase()));
};

// Loads the account's config for one party type; `reload` after saving.
export const usePartyFormConfig = (type: PartyType) => {
    const dispatch = useAppDispatch();
    const accountId = useAppSelector((state: any) => state.auth.selectedAccount?._id);
    const [config, setConfig] = useState<PartyFormConfig>(() => resolvePartyForm(null, type));

    const reload = useCallback(async () => {
        if (!accountId) return;
        try {
            const res: any = await dispatch(fetchAccountSettings(accountId)).unwrap();
            setConfig(resolvePartyForm(res?.settings, type));
        } catch { /* keep defaults */ }
    }, [accountId, type, dispatch]);

    useEffect(() => { reload(); }, [reload]);

    return { config, reload, isHidden: (field: string) => config.hiddenFields.includes(field) };
};
